import React, { useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';

import { ReactComponent as ActionEditIcon } from '../../assets/icons/action-edit.svg';
import { ReactComponent as ActivityIcon } from '../../assets/icons/activity-icon.svg';
import { ReactComponent as AddUserIcon } from '../../assets/icons/add-user.svg';
import { ReactComponent as DeleteIcon } from '../../assets/icons/delete-icon.svg';
import { ReactComponent as GridList } from '../../assets/icons/grid-list.svg';
import { ReactComponent as RoleIconActiveDark } from '../../assets/icons/iconsDark/role-icon-active.svg';
import { ReactComponent as RoleIconDark } from '../../assets/icons/iconsDark/role-icon-dark.svg';
import { ReactComponent as UserIconActiveDark } from '../../assets/icons/iconsDark/user-icon-active-dark.svg';
import { ReactComponent as UserIconDark } from '../../assets/icons/iconsDark/user-icon-dark.svg';
import { ReactComponent as ActiveRoleIcon } from '../../assets/icons/role-active.svg';
import { ReactComponent as RoleIcon } from '../../assets/icons/role.svg';
import { ReactComponent as TableList } from '../../assets/icons/table-list.svg';
import { ReactComponent as ActiveUserIcon } from '../../assets/icons/user-active.svg';
import { ReactComponent as UserIcon } from '../../assets/icons/user.svg';
import DefaultApiLimit from '../../config/defaultLimit';
import ThemeContext from '../../contexts/theme-context/ThemeContext';
import { RequestUserInterface, UserInterFace } from '../../interface/user-management.interface';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { setNotificationDetails } from '../../redux/slice/NotificationSlice';
import {
  fetchAllUsers,
  fetchListFilters,
  fetchUserActivityLogs,
  setSortData,
  setUserFilterData,
  updateUserFilters,
} from '../../redux/slice/UserManagementSlice';
import CardBox from '../../shared-components/card-box/CardBox';
import CustomTableHeader from '../../shared-components/custom-table/components/TableHeader';
import CustomTable from '../../shared-components/custom-table/CustomTable';
import DeleteWarning from '../../shared-components/delete-warning/DeleteWarning';
import ExportButton from '../../shared-components/export-button/ExportButton';
import ModalBox from '../../shared-components/modal-box/ModalBox';
import PageTitle from '../../shared-components/page-title/PageTitle';
import Pagination from '../../shared-components/pagination/Pagination';
import { GetParametersFromCommonFilters } from '../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../shared-functions/ConvertUTCtoDate';
import { splitArrayIntoChunks } from '../../shared-functions/DataTime';
import { exportCSV, exportPDF } from '../../shared-functions/ExportFile';
import userApi from '../../utils/api/users';

import ActiveLogCard from './component/active-log-card';
import AddUser from './component/AddUser';
import EditUser from './component/EditUser';
import MultiButtonGroup from './component/MultiButtonGroup';
import UserCardBody from './component/user-card-body';

interface ModalBoxDetail {
  title: string | null;
  content: JSX.Element | null;
  notificationModal: boolean;
  header: boolean;
}
/**
 * UserManagement component
 *
 * This component serves as the main entry point for the user management page.
 * It renders the user management interface.
 * @returns {JSX.Element} A JSX element representing the user management page.
 */
function UserManagement() {
  const navigate = useNavigate();
  const { theme } = useContext(ThemeContext);
  const dispatch = useAppDispatch();
  const { users, page, loading, limit, totalPages, totalResults, filters, filterListLoading } =
    useAppSelector((state) => state.users);
  const { currentFacilityTagsList } = useAppSelector((state) => state.facilityTags);
  const [modalBoxStatus, setModalBoxStatus] = useState<'open' | 'close'>('close');
  const [openTab, setOpenTab] = useState<0 | 1>(0);
  const [activeLogModal, setActiveLogModal] = useState(false);
  const [position, setPosition] = useState<any>({});
  const [fullWidth, setFullWidth] = useState<any>({});
  const [modalBoxDetail, setModalBoxDetail] = useState<ModalBoxDetail>({
    title: '',
    content: null,
    notificationModal: false,
    header: false,
  });
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const userList = ['User', 'Role'];
  const { sortBy, sortField } = useAppSelector((state) => state.users);

  /**
   * Handle the fetch data for Api call function
   * @param data
   */
  const fetchData = (data: RequestUserInterface) => {
    const selectedFacility = currentFacilityTagsList.find((item) => item.isSelected);

    if (selectedFacility?._id && !loading) {
      dispatch(
        fetchAllUsers({
          ...data,
        }),
      );
    }
  };

  /**
   * Handle the submit function
   */
  const submit = () => {
    setModalBoxDetail({
      title: 'Add User',
      content: <AddUser handleCancel={() => setModalBoxStatus('close')} />,
      notificationModal: false,
      header: true,
    });
    setModalBoxStatus('open');
  };

  /**
   * Handle the toggle button
   * @param value
   */
  const onToggle = (value: number | string) => {
    if (value === 1) {
      navigate(`../role`);
    }
  };

  /**
   * User details view function
   */
  const viewActiveLogFunction = (currentData: UserInterFace, event?: React.MouseEvent) => {
    if (event) {
      setActiveLogModal(true);
      if (event.clientY > 320) {
        setPosition({ top: `${event.clientY - 320}px`, '--card-left-position': '5%' });
      } else {
        setPosition({
          top: `${event.clientY + 40}px`,
          '--card-bottom-position': '91%',
          '--card-left-position': '5%',
        });
      }
      dispatch(fetchUserActivityLogs(currentData.id));
    }
  };

  /**
   * User details view function
   */
  const viewCardActiveFunction = (
    currentData: UserInterFace,
    userIndex: number,
    event?: React.MouseEvent,
  ) => {
    if (event) {
      let cardLeftPosition = '13%';
      let gridRowStart = 1;
      let rowCount = 4;
      const array = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9];
      const array2 = [
        [0, 4, 8],
        [1, 5, 9],
        [2, 6],
        [3, 7],
      ];
      if (window.innerWidth > 1571) {
        rowCount = 4;
      } else if (window.innerWidth > 1333) {
        rowCount = 3;
      } else if (window.innerWidth > 945) {
        rowCount = 2;
      } else {
        rowCount = 1;
      }
      const chunks = splitArrayIntoChunks(array, rowCount);
      cardLeftPosition = `${(100 * userIndex) / rowCount / 2}%`;
      gridRowStart = chunks.findIndex((value) => value.includes(userIndex)) + 1;
      cardLeftPosition = `${(100 * gridRowStart) / rowCount / 2}%`;

      if (rowCount === 1) {
        cardLeftPosition = '50%';
      } else if (rowCount === 2) {
        cardLeftPosition = userIndex % 2 !== 0 ? '75%' : '25%';
      } else if (rowCount === 3) {
        if (userIndex && [0, 3, 6, 9].includes(userIndex)) {
          cardLeftPosition = '20%';
        } else if (userIndex && [1, 4, 7].includes(userIndex)) {
          cardLeftPosition = '50%';
        } else if (userIndex && [2, 5, 8].includes(userIndex)) {
          cardLeftPosition = '80%';
        }
      } else if (rowCount === 4) {
        if (userIndex && array2[0].includes(userIndex)) {
          cardLeftPosition = '13%';
        } else if (userIndex && array2[1].includes(userIndex)) {
          cardLeftPosition = '37.5%';
        } else if (userIndex && array2[2].includes(userIndex)) {
          cardLeftPosition = '63.5%';
        } else if (userIndex && array2[3].includes(userIndex)) {
          cardLeftPosition = '86%';
        }
      }
      setPosition({
        '--card-left-position': cardLeftPosition,
        top: '0px',
        left: '0',
        position: 'relative',
      });
      setActiveLogModal(true);
      setFullWidth({
        gridRowStart,
      });
      dispatch(fetchUserActivityLogs(currentData.id));
    }
  };

  /**
   * Delete user confirm function
   */
  const confirmUserDelete = (accountId: string) => {
    if (accountId) {
      userApi
        .deleteUser(accountId)
        .then(() => {
          setModalBoxStatus('close');
          fetchData({
            page,
            limit,
          });
        })
        .catch((error) => {
          dispatch(
            setNotificationDetails({
              type: 'error',
              notificationDetails: {
                statusCode: error?.response?.data?.code || 200,
                message: error?.response?.data?.message || 'Something went wrong!',
              },
            }),
          );
        });
    }
  };

  /**
   * delete modal function
   * @param id
   */
  const deleteUserFunction = (id: string) => {
    setModalBoxStatus('open');
    setModalBoxDetail({
      title: null,
      header: false,
      content: (
        <DeleteWarning
          selectedRowId={id}
          title="Are you sure want to delete this user?"
          description="Deleting your account is permanent and cannot be undone. All the data, settings, and transaction history will be lost. Are you sure you want proceed?"
          cancelButtonOnClick={() => setModalBoxStatus('close')}
          submitSvgIcon={<DeleteIcon />}
          submitButtonOnClick={(userId) => userId && confirmUserDelete(userId)}
        />
      ),
      notificationModal: true,
    });
  };

  /**
   * Handle the change page function
   * @param pageNumber
   */
  const handlePageChange = (pageNumber: number) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      sortField,
      sortBy,
      page: pageNumber,
      limit,
    });
  };

  /**
   * Handle the change limit function
   * @param limitCount
   */
  const handleLimitChange = (limitCount: number) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      page: DefaultApiLimit.page,
      limit: limitCount,
    });
  };

  /**
   * Handle the sort field function
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    if (Object.keys(sortData).length > 0) {
      dispatch(setUserFilterData(sortData));
      dispatch(setSortData(sortData));
      fetchData({
        ...queryParameters,
        sortBy: sortData.sortBy,
        sortField: sortData.sortField,
        page: DefaultApiLimit.page,
        limit,
      });
    }
  };

  /**
   * getIcons
   */
  const getIcons = () => ({
    defaultIcons: [
      theme === 'light' ? <UserIcon /> : <UserIconDark />,
      theme === 'light' ? <RoleIcon /> : <RoleIconDark />,
    ],
    activeIcons: [
      theme === 'light' ? <ActiveUserIcon /> : <UserIconActiveDark />,
      theme === 'light' ? <ActiveRoleIcon /> : <RoleIconActiveDark />,
    ],
  });
  const { defaultIcons, activeIcons } = getIcons();

  /**
   * Handle the edit user function
   * @param val
   */
  const editUser = (currentData: UserInterFace) => {
    setModalBoxDetail({
      title: 'Edit User',
      content: (
        <EditUser handleCancel={() => setModalBoxStatus('close')} selectedUser={currentData} />
      ),
      notificationModal: false,
      header: true,
    });
    setModalBoxStatus('open');
  };

  const column = [
    {
      label: 'S. No',
      accessorKey: 'sno' as keyof UserInterFace,
      /**
       * Handle the custom data function
       * @param station
       * @param index
       */
      customData: (station: UserInterFace, index: number) => (
        <div className="table-row-each-data">{index + 1}</div>
      ),
      columnWidth: 40,
    },
    {
      label: 'Date',
      accessorKey: 'date' as keyof UserInterFace,
      sortAllow: true,
    },
    {
      label: 'User name',
      accessorKey: 'name' as keyof UserInterFace,
      sortAllow: true,
    },
    {
      label: 'Role',
      accessorKey: 'roleName' as keyof UserInterFace,
      sortAllow: true,
    },
    {
      label: 'Contact',
      accessorKey: 'email' as keyof UserInterFace,
      sortAllow: true,
    },
    {
      label: 'Status',
      accessorKey: 'status' as keyof UserInterFace,
      sortAllow: true,
      position: 'center',
      /**
       * Data custom function
       * @param currentRow
       */
      customData: (currentRow: UserInterFace) =>
        currentRow.deletedAt ? (
          <div className="user-management-status in-active">
            <div className="status-circle" />
            <p>Inactive</p>
          </div>
        ) : (
          <div className="user-management-status active">
            <div className="status-circle" />
            <p>Active</p>
          </div>
        ),
    },
    {
      label: 'Actions',
      accessorKey: '' as keyof UserInterFace,
      columnWidth: 100,
      /**
       * Action button in table
       * @param station
       * @param index
       */
      customData: (currentRow: UserInterFace) => (
        <div className="action-button-group">
          <div
            className="action-button-item view-color"
            onClick={(event) => viewActiveLogFunction(currentRow, event)}
            role="presentation">
            <ActivityIcon />
          </div>
          <div
            className="action-button-item"
            onClick={() => editUser(currentRow)}
            role="presentation">
            <ActionEditIcon />
          </div>
          <div
            className="action-button-item delete-color"
            onClick={() => deleteUserFunction(currentRow.id)}
            role="presentation">
            <DeleteIcon />
          </div>
        </div>
      ),
    },
  ];

  /**
   * Export data function
   */
  const exportDataPdfOrCSV = (type: string) => {
    setExportLoader(true);
    userApi
      .fetchUsersExport({ ...filters })
      .then((response) => {
        // Convert data to CSV format
        const headers = ['Date', 'User name', 'Role', 'Contact', 'Status'];
        const rows = response.data.results.map((row: UserInterFace) => [
          row.createdAt ? ConvertUTCtoDate(row.createdAt) : '',
          row.name || '',
          row.role?.name || '',
          row.email || '',
          row.status ? 'Active' : 'Inactive',
        ]);
        setExportLoader(false);
        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'users-list.csv');
          } else {
            exportPDF(headers, rows, 'users-list.pdf');
          }
        }
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 404,
              message: error?.response?.data?.message || 'Something went wrong!',
            },
          }),
        );
      });
  };

  useEffect(() => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      limit: DefaultApiLimit.limit,
      page: DefaultApiLimit.page,
    });
  }, [currentFacilityTagsList, filters]);

  return (
    <div className="user-management-content">
      <ModalBox
        open={modalBoxStatus === 'open'}
        title={modalBoxDetail.title}
        content={modalBoxDetail.content}
        closeModal={() => setModalBoxStatus('close')}
        modalBoxAnimation="modalAnimateToLeft"
        header={modalBoxDetail.header}
        notification={modalBoxDetail.notificationModal}
      />
      <div className="user-management-header">
        <PageTitle
          title="User & Role management"
          buttonLabel="Add User"
          buttonSvgIcon={<AddUserIcon />}
          handleToggle={onToggle}
          icons={defaultIcons}
          labels={['User', 'Role']}
          activeIcons={activeIcons}
          buttonType="back"
          enableToggleButton
          toggleList={userList}
          onClickButton={submit}
          defaultIndex={0}
        />
      </div>
      <div>
        {openTab === 0 && (
          <>
            <CustomTable
              paginate
              loading={loading}
              limit={limit}
              header={column}
              data={users}
              currentPage={page}
              sortData={{
                sortBy,
                sortField
              }}
              handleSortChange={handleSortFunction}
              totalPages={totalPages}
              totalResults={totalResults}
              handlePageChange={handlePageChange}
              handleLimitChange={handleLimitChange}
              page={page}
              sizeChangeButtonRequired
              rightTopContent={
                <MultiButtonGroup
                  activeTab={0}
                  buttonList={[
                    {
                      icon: <TableList />,
                      /**
                       * Button function
                       * @returns
                       */
                      function: () => setOpenTab(0),
                    },
                    {
                      icon: <GridList />,
                      /**
                       * Button function
                       * @returns
                       */
                      function: () => setOpenTab(1),
                    },
                  ]}
                />
              }
              footerLeft={
                <div className="table-bottom-button-list">
                  <div className="table-button-item">
                    <ExportButton
                      label="Export Data"
                      buttonList={[
                        {
                          key: 'pdf',
                          allow: true,
                          /**
                           * Export pdf function
                           * @returns
                           */
                          onClickFunction: () => exportDataPdfOrCSV('pdf'),
                        },
                        {
                          key: 'csv',
                          allow: true,
                          /**
                           * Export csv function
                           * @returns
                           */
                          onClickFunction: () => exportDataPdfOrCSV('csv'),
                        },
                      ]}
                      disabled={users.length === 0}
                      loader={exportLoader}
                    />
                  </div>
                </div>
              }
              topHeader={false}
              filters={filters}
              filtersToDispatch={updateUserFilters}
              filterListLoading={filterListLoading}
              filterListToDispatch={fetchListFilters}
            />
            <ActiveLogCard
              closeModal={() => setActiveLogModal(false)}
              activeLogModal={activeLogModal}
              position={position}
            />
          </>
        )}
        {openTab === 1 && (
          <div className="card-details">
            <CustomTableHeader
              filters={filters}
              filtersToDispatch={updateUserFilters}
              filterListToDispatch={fetchListFilters}
              filterListLoading={filterListLoading || false}
              rightTopContent={
                <MultiButtonGroup
                  activeTab={1}
                  buttonList={[
                    {
                      icon: <TableList />,
                      /**
                       * Button function
                       * @returns
                       */
                      function: () => setOpenTab(0),
                    },
                    {
                      icon: <GridList />,
                      /**
                       * Button function
                       * @returns
                       */
                      function: () => setOpenTab(1),
                    },
                  ]}
                />
              }
            />
            <div className="card-list">
              {activeLogModal && (
                <div className="fixed-grid" style={fullWidth}>
                  <ActiveLogCard
                    closeModal={() => setActiveLogModal(false)}
                    activeLogModal={activeLogModal}
                    position={position}
                  />
                </div>
              )}
              {users.map((value: UserInterFace, index: number) => (
                <CardBox>
                  <UserCardBody
                    userDetails={value}
                    activeLog={(userId, event) => viewCardActiveFunction(userId, index, event)}
                    editFunction={editUser}
                    menuList={[
                      {
                        content: (
                          <div className="text-danger flex-row">
                            {' '}
                            <DeleteIcon /> Delete
                          </div>
                        ),
                        /**
                         * fasdf
                         */
                        function: deleteUserFunction,
                      },
                    ]}
                  />
                </CardBox>
              ))}
            </div>
            <div className="pagination">
              <Pagination
                activePage={page}
                handlePageChange={handlePageChange}
                size={totalPages}
                totalResults={totalResults}
                limit={limit}
                handleLimitChange={handleLimitChange}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default UserManagement;
